import type { PaymentElementTabs, PaymentOrder } from '@shared/elements/common/payment/types';
import type { StoreLinkKey } from '@shared/elements/payment-result/definitions';

type CSSStyle = {
  [P in keyof CSSStyleDeclaration]?: CSSStyleDeclaration[P];
};

export type Style = CSSStyle & { hover?: CSSStyle; };

export type ScreenSize = 'mobile' | 'tablet' | 'desktop';

export type Directions = 'top' | 'bottom' | 'left' | 'right';

export type TextStyleProperty = 'bold' | 'italic' | 'underline' | 'strikeThrough';

export type AlignmentPosition = 'left' | 'center' | 'right';
export type VerticalPosition = 'top' | 'bottom';

export type PlanSelectionTextTypes = 'text1' | 'text2' | 'text3' | 'priceText1' | 'priceText2';

export type CountdownTextTypes = 'labelText' | 'countdownText' | 'countdownNumber';
export type CountdownTypes = 'grid' | 'textBased' | 'boxed';
export type CountdownFormats = "DHMS" | "DHM" | "HMS" | "HM" | "MS";

export type Spacing = Record<ScreenSize, Record<Directions, number>>;

export type ScreenSizeStyle = Record<ScreenSize, {
  width: number | string;
  height: number | string;
  autoHeight?: boolean;
  objectFit: string;
}>;

export type QuestionTypes =
  | 'selection'
  | 'imageselection'
  | 'surveyprogress'
  | 'infopage'
  | 'timeentry'
  | 'dateentry'
  | 'locationpicker'
  | 'singlerowtext'
  | 'file'
  | 'numericinput'
  | 'weightinput'
  | 'heightinput'
  | 'scaledchoice';

export type OptionType = {
  label: string;
  value: number | string;
};

export type GeneralTextOptions = {
  customizeFontType?: boolean;
  fontFamily?: string;
  customizeTextColor?: boolean;
  colors?: {
    regular: string;
    hover?: string;
    selected?: string;
  };
  fontSize?: number;
  textStyle?: Record<TextStyleProperty, boolean>;
  alignment?: AlignmentPosition;
}

export type LabelOptions = {
  show?: boolean;
  customizeFontType?: boolean;
  fontFamily?: string;
  customizeFontColor?: boolean;
  color?: string;
  fontSize?: number;
  textStyle?: Record<TextStyleProperty, boolean>;
};

export type PaymentLabelOptions = LabelOptions & { totalLineColor: string; };

export type PaymentConsentOptions = LabelOptions;

export type PlanSelectionText = {
  customizeFontType?: boolean;
  fontFamily?: string;
  customizeTextColor?: boolean;
  colors?: {
    regular: string;
    hover: string;
    selected: string;
  };
  fontSize?: number;
  textStyle?: Record<TextStyleProperty, boolean>;
  alignment?: AlignmentPosition;
}

export type PlanSelectionRibbon = {
  show?: boolean;
  customizeFontType?: boolean;
  fontFamily?: string;
  colors: {
    text: string;
    background: string;
  };
  fontSize?: number;
  position?: AlignmentPosition;
  verticalPosition?: VerticalPosition;
  radius?: number;
  textStyle?: Record<TextStyleProperty, boolean>;
}

export type PlanSelectionHighlight = Omit<PlanSelectionRibbon, 'position' | 'radius'>;

export type QuestionOptions = {
  isTitle?: boolean;
  isDesc?: boolean;
}

export type SelectionButtonsOptions = {
  fontFamily: string;
  fontSize?: number;
  color?: {
    regular: string;
    hover: string;
    selected: string;
  };
  textAlign: AlignmentPosition;
  textStyle?: {
    bold: boolean;
    italic: boolean;
    underline: boolean;
    strikeThrough: boolean;
  };
  background?: {
    regular: string;
    hover: string;
    selected: string;
  };
  border?: {
    regular: string;
    hover: string;
    selected: string;
    width: number;
    radius: number;
  };
}

export enum ScaledChoiceType {
  NUMERIC = 'numeric',
  FACE = 'face',
  THUMBS = 'thumbs',
  CUSTOM = 'custom'
}

export type GeneralOptions = {
  visible: boolean;
  hideable?: boolean;
  allowLinks?: boolean;
  editable?: boolean;
  label?: LabelOptions;
}

export type ElementOptions = {
  default: QuestionOptions & GeneralOptions;

  /*
    BASIC ELEMENTS
  */

  text: ElementOptions['default'] & {
    customizeFontType?: boolean;
    customizeFontColor?: boolean;
    questionTitle?: boolean;
    questionDesc?: boolean;
    padding?: Spacing;
    margin?: Spacing;
    maxWidthUnit?: '%' | 'px';
    maxWidth?: {
      desktop: number;
      tablet: number;
      mobile: number;
    }
    responsiveFontSize?: {
      tablet: {
        active: boolean;
        size: number;
      }
      mobile: {
        active: boolean;
        size: number;
      }
    }
  };

  'conditional-text': ElementOptions['default'] & Pick<ElementOptions['text'], 'maxWidthUnit' | 'maxWidth' | 'responsiveFontSize' | 'padding' | 'margin'> & {
    conditionalOptions: Record<string, {
      customizeFontType: boolean;
      customizeFontColor: boolean;
      responsiveFontSize: ElementOptions['text']['responsiveFontSize'];
    }>;
  };

  button: ElementOptions['text'] & {
    customizeButtonColor?: boolean;
    customizeCornerRadius?: boolean;
  };

  'conditional-button': Omit<ElementOptions['button'], 'maxWidthUnit' | 'maxWidth' | 'responsiveFontSize'> & {
    conditionalOptions: Record<string, {
      customizeFontType: boolean;
      customizeFontColor: boolean;
      style: Style;
    }>;
  };

  video: ElementOptions['default'];

  image: ElementOptions['default'] & {
    fullSize?: boolean;
    padding?: Spacing;
    margin?: Spacing;
    autoplay?: boolean;
    loop?: boolean;
    playWithSound?: boolean;
    showControlPanel?: boolean;
    videoEmbedURL?: string;
    isVideoAllowed?: boolean;
    mediaType?: 'image' | 'video' | 'embed_youtube' | 'embed_vimeo';
    videoSrc?: {
      id?: number | string;
      cover?: string;
      name?: string;
      url?: string;
      size?: number;
    };
    videoWidth?: number;
    videoHeight?: number;
    videoFullSize?: boolean;
    sizes: ScreenSizeStyle;
  };

  plain: ElementOptions['default'];

  endingprogress: ElementOptions['default'] & {
    padding?: Spacing;
    margin?: Spacing;
    bar?: {
      customize: boolean;
      bgColor: string;
      trackColor: string;
    };
    steps?: {
      customizeFontType: boolean;
      customizeFontColor: boolean;
      fontFamily: string;
      fontSize: number;
      checkmarkUrl: string;
      count: number;
      maxStepsCount: number;
      text: {
        regular: string;
        inProgress: string;
        done: string;
      };
      alignment: AlignmentPosition;
    };
    waitTime?: number;
  };

  translation: ElementOptions['text'] & {
    textStyle?: Record<TextStyleProperty, boolean>;
    params?: Record<string, any>;
  };

  applinks: ElementOptions['default'] & {
    dark?: boolean;
    show?: Record<StoreLinkKey, boolean>;
    useDeeplink?: Record<StoreLinkKey, boolean>;
    activationButton?: {
      show: boolean;
    }
  };

  testimonials: ElementOptions['default'] & {
    customizeFontColor?: boolean;
    customizeFontType?: boolean;
    customizeBGColor?: boolean;
    customizeCornerRadius?: boolean;
    customizeBorderColor?: boolean;
    slideDuration?: number;
    count?: {
      min: number;
      max: number;
      current: number;
    };
    bgColor?: string;
    border?: {
      width: number;
      color: string;
      radius: number;
    };
    show?: {
      image: boolean;
      rate: boolean;
    }
  };

  'moneyback': ElementOptions['default'] & {
    card?: {
      background?: {
        color: string;
        opacity: number;
      },
      border?: {
        width: number;
        color: string;
        radius: number;
      };
    };
    icon?: string;
  }

  faq: ElementOptions['default'] & {
    customizeFontColor?: boolean;
    customizeFontType?: boolean;
    customizeBGColor?: boolean;
    customizeCornerRadius?: boolean;
    customizeBorderColor?: boolean;
    count?: {
      min: number;
      max: number;
      current: number;
    };
    bgColor?: string;
    border?: {
      width: number;
      color: string;
      radius: number;
    };
  };

  countdown: ElementOptions['default'] & {
    labelText: GeneralTextOptions;
    countdownText: GeneralTextOptions;
    countdownNumber: GeneralTextOptions;
    type: CountdownTypes;
    format: CountdownFormats;
    startTime: number;
    background?: {
      color: string;
      opacity: number;
    };
    border?: {
      radius: number;
      width: number;
      color: string;
    };
    padding?: Spacing;
    margin?: Spacing;
  };

  /*
    QUESTION ELEMENTS
  */

  selection: ElementOptions['default'] & Pick<ElementOptions['button'], 'customizeCornerRadius' | 'customizeFontType' | 'customizeFontColor'> & {
    customizeBGColor?: boolean;
    customizeBorderColor?: boolean;
    required?: boolean;
    multiple?: {
      active: boolean;
      min: {
        active: boolean;
        limit: number;
      };
      max: {
        active: boolean;
        limit: number;
      };
    };
    columns?: {
      default: number;
      max: number;
    };
    buttons?: SelectionButtonsOptions;
  } & {
    supersizeImages?: boolean;
    showLabels?: boolean;
    imageAlign?: 'left' | 'center';
  };

  imageselection: ElementOptions['selection'];

  surveyprogress: ElementOptions['default'] & Pick<ElementOptions['text'], 'customizeFontType' | 'customizeFontColor'> & {
    activeColor?: string;
    showPercentage?: boolean;
    bar?: {
      customize: boolean;
      bgColor: string;
      trackColor: string;
    };
    backButton?: {
      show: boolean;
      icon: string;
    };
    skipButton?: {
      show: boolean;
      icon: string;
    };
  };

  infopage: ElementOptions['default'];

  dateentry: ElementOptions['default'] & {
    required?: boolean;
    mode?: 'picker' | 'dropdown-horizontal' | 'dropdown-vertical' | 'slider';
    showMonthNames?: boolean;
    defaultValue?: string;
    minValue?: string;
    maxValue?: string;
    label?: LabelOptions;
    isUSDateFormat?: boolean;
  };

  timeentry: ElementOptions['default'] & {
    required?: boolean;
    mode?: 'picker' | 'dropdown-horizontal';
    defaultValue?: string;
    twelveHourFormat?: boolean;
    label?: LabelOptions;
    dontKnowButton?: {
      show: boolean;
      icon: string;
    };
  };

  locationpicker: ElementOptions['default'] & {
    required?: boolean;
    type?: 'city' | 'country';
    input?: {
      focusBorderColor?: string;
      focusBoxShadow?: string;
    };
    label?: LabelOptions;
  };

  singlerowtext: ElementOptions['default'] & {
    required?: boolean;
    input?: {
      showLabels?: boolean;
      focusBorderColor?: string;
      focusBoxShadow?: string;
      min?: {
        active: boolean;
        limit: number;
      };
      max?: {
        active: boolean;
        limit: number;
      };
    };
    label?: LabelOptions;
  };

  file: ElementOptions['default'] & {
    required?: boolean;
    uploadType?: string,
    multiple?: boolean,
    maxFileCount?: number,
    minFileCount?: number,
    isAllowImg?: boolean,
    isAllowVideo?: boolean,
    imgSize?: number,
    videoSize?: number,
    selectedImgType?: string[],
    imgTypes?: OptionType[],
    selectedVideoType?: string[],
    videoTypes?: OptionType[]
  };

  numericinput: ElementOptions['default'] & {
    required?: boolean;
    decimal?: boolean;
    zero?: boolean;
    defaultValue?: number | string;
    showInfoTag?: boolean;
    customizeTagTextColor?: boolean;
    infoTagTitleColor?: string;
    infoTagDescColor?: string;
    input?: {
      showLabels?: boolean;
      focusBorderColor?: string;
      focusBoxShadow?: string;
      min?: {
        active: boolean;
        limit: number | string | null;
      };
      max?: {
        active: boolean;
        limit: number | string | null;
      };
    };
    label?: LabelOptions;
  };

  weightinput: ElementOptions['default'] & {
    required?: boolean;
    imperial?: boolean;
    selectionByCountry?: boolean;
    defaultValue?: number | string;
    infoTag?: {
      show?: boolean;
      type?: 'static' | 'bmi' | 'comparison' | 'comparisonBmi'
    };
    customizeTagTextColor?: boolean;
    infoTagTitleColor?: string;
    infoTagDescColor?: string;
    bmiCalculation?: {
      active?: boolean;
      heightinput?: string | number | null;
      showRecommendedRange?: boolean;
    };
    weightComparison?: {
      active?: boolean;
      weightinput?: string | number | null;
    }
    input?: {
      focusBorderColor?: string;
      focusBoxShadow?: string;
      min?: {
        active: boolean;
        limit: number | string | null;
      };
      max?: {
        active: boolean;
        limit: number | string | null;
      };
    };
  };

  heightinput: ElementOptions['default'] & {
    required?: boolean;
    imperial?: boolean;
    selectionByCountry?: boolean;
    defaultValue?: number | string;
    showInfoTag?: boolean;
    customizeTagTextColor?: boolean;
    infoTagTitleColor?: string;
    infoTagDescColor?: string;
    input?: {
      showLabels?: boolean;
      focusBorderColor?: string;
      focusBoxShadow?: string;
      min?: {
        active: boolean;
        limit: number | string | null;
      };
      max?: {
        active: boolean;
        limit: number | string | null;
      };
    };
  };

  scaledchoice: ElementOptions['default'] & {
    required?: boolean;
    type?: ScaledChoiceType;
    anchor?: {
      show: boolean;
      position: 'top' | 'bottom';
    };
    customizeBorderColor?: boolean;
    customizeBgColor?: boolean;
    customizeFontType?: boolean;
    customizeTextColor?: boolean;
    buttons?: SelectionButtonsOptions;
    scaleSize?: {
      min: number;
      max: number;
      current: number;
    };
  };

  /*
    REGISTRATION
  */

  registration: ElementOptions['default'] & {
    input?: {
      showIcon?: boolean;
    };
    socialLogin?: {
      google: boolean;
      apple: boolean;
      facebook: boolean;
    };
    socialLoginCustomize?:{
      status: boolean;
      borderRadius: number | string;
    }
    label?: LabelOptions;
  };

  /*
    PAYWALL
  */

  'plan-selection': ElementOptions['default'] & {
    design?: {
      bgColors?: {
        regular: string;
        hover: string;
        selected: string;
      };
      priceCard?: {
        show?: boolean;
        customizeColor?: boolean;
        colors?: {
          regular: string;
          hover: string;
          selected: string;
        };
      },
      border?: {
        customizeColor?: boolean;
        colors?: {
          regular: string;
          hover: string;
          selected: string;
        };
        radius?: number;
        width?: number;
      }
      verticalPadding?: {
        min?: number;
        max?: number;
        current: number;
      }
    }
    text?: Record<PlanSelectionTextTypes, PlanSelectionText>;
    plans?: {
      count?: {
        min: number;
        max: number;
        current: number;
      };
      defaultSelection?: number;
      selectedPackages?: number[];
    };
    ribbon?: PlanSelectionRibbon[];
    highlight?: PlanSelectionHighlight[];
  };


  /*
    PAYMENT
  */

  payment: ElementOptions['default'] & {
    defaultTab?: PaymentElementTabs;
    label?: PaymentLabelOptions;
    consent?: PaymentConsentOptions;
    darkModeOnTabButtons?: boolean;
    layout?: 'tabs' | 'accordion';
    order?: PaymentOrder;
  };

  'payment-with-tabs': ElementOptions['payment'];


  /*
    PAYMENT SUCCESS
  */

 'payment-result': ElementOptions['default'] & {
    cards?: {
      background?: {
        color: string;
        opacity: number;
      },
      border?: {
        width: number;
        color: string;
        radius: number;
      };
    };
    icons?: {
      success: string;
      warning: string;
    };
    appLinks?: Omit<ElementOptions['applinks'], 'visible'>;
  }
}

export type ElementTypes = keyof ElementOptions;
